import React from 'react';
import { Box, Container, Grid, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { containerVariants, itemVariants } from '../../styled/StyledComponents';
import { CONTACT_EMAIL_ADVISORY } from '../../../config/links';

const ADVISORY_ACCENT = '#a78bfa';

const SectionRoot = styled(Box)({
  backgroundColor: '#1a1a2e',
  color: '#FFFFFF',
  padding: '140px 0',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-30%',
    right: '-15%',
    width: '60%',
    height: '160%',
    background: `radial-gradient(circle, ${ADVISORY_ACCENT}22 0%, transparent 60%)`,
    filter: 'blur(80px)',
    zIndex: 0,
  },
});

const Overline = styled(Typography)({
  color: ADVISORY_ACCENT,
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  marginBottom: 16,
});

const Headline = styled(Typography)({
  color: '#FFFFFF',
  fontSize: 'clamp(2rem, 3.5vw, 3rem)',
  fontWeight: 700,
  lineHeight: 1.15,
  marginBottom: 24,
  letterSpacing: '-0.01em',
});

const Body = styled(Typography)({
  color: 'rgba(255,255,255,0.72)',
  fontSize: '1.05rem',
  lineHeight: 1.7,
  marginBottom: 20,
});

const ServiceList = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: 0,
});

const ServiceItem = styled('li')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: '16px 0',
  borderBottom: '1px solid rgba(167, 139, 250, 0.15)',
  fontSize: '1rem',
  lineHeight: 1.45,
  color: 'rgba(255,255,255,0.88)',
  '&:last-of-type': {
    borderBottom: 'none',
  },
});

const ServiceBullet = styled('span')({
  color: ADVISORY_ACCENT,
  fontSize: '1rem',
  marginTop: 2,
  flexShrink: 0,
});

const Cta = styled(Button)({
  backgroundColor: ADVISORY_ACCENT,
  color: '#1a1a2e',
  padding: '14px 32px',
  borderRadius: '4px',
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: `0 8px 24px ${ADVISORY_ACCENT}44`,
  transition: 'all 0.3s ease',
  marginTop: 32,
  '&:hover': {
    backgroundColor: '#c4b5fd',
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${ADVISORY_ACCENT}66`,
  },
});

const AdvisorySection = () => {
  const { t } = useTranslation();
  const services = t('landing.advisory.services', { returnObjects: true }) || [];

  return (
    <SectionRoot id="advisory-section">
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={containerVariants}
        >
          <Grid container spacing={{ xs: 6, md: 10 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Overline>{t('landing.advisory.overline')}</Overline>
                <Headline>{t('landing.advisory.title')}</Headline>
                <Body>{t('landing.advisory.body1')}</Body>
                <Body>{t('landing.advisory.body2')}</Body>
                <Cta
                  component="a"
                  href={`mailto:${CONTACT_EMAIL_ADVISORY}`}
                  endIcon={<ArrowForwardIcon />}
                >
                  {t('landing.advisory.cta')}
                </Cta>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <ServiceList>
                  {Array.isArray(services) &&
                    services.map((service, idx) => (
                      <ServiceItem key={idx}>
                        <ServiceBullet>✦</ServiceBullet>
                        <span>{service}</span>
                      </ServiceItem>
                    ))}
                </ServiceList>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </SectionRoot>
  );
};

export default AdvisorySection;
